import { Injectable, OnDestroy } from '@angular/core';
import MapView from '@arcgis/core/views/MapView';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Graphic from '@arcgis/core/Graphic';
import Sketch from '@arcgis/core/widgets/Sketch';
import * as geodeticLengthOperator from '@arcgis/core/geometry/operators/geodeticLengthOperator';
import * as geodeticAreaOperator from '@arcgis/core/geometry/operators/geodeticAreaOperator';
import * as lengthOperator from '@arcgis/core/geometry/operators/lengthOperator';
import * as areaOperator from '@arcgis/core/geometry/operators/areaOperator';
import TextSymbol from '@arcgis/core/symbols/TextSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import Color from '@arcgis/core/Color';
import Point from '@arcgis/core/geometry/Point';
import Polyline from '@arcgis/core/geometry/Polyline';
import Polygon from '@arcgis/core/geometry/Polygon';
import { ProjectionService } from '../../../../shared/services/projection-service';
import { IHandle } from '../../../../shared/types/i-handle';
import { MeasureUnitEnum } from './measure-unit-enum';
import { MeasureToolEnum } from './measure-tool-enum';

export interface MeasureRecord {
  id: number;
  type: MeasureToolEnum.Distance | MeasureToolEnum.Area | MeasureToolEnum.Location;
  geometry: Polyline | Polygon | Point;
  graphic: Graphic;
  labelGraphic: Graphic | null;
}

export type OnResultCallback = (id: number, rawValue: number, unit: MeasureUnitEnum, label: string, perimeterRawValue?: number) => void;
export type OnLiveUpdateCallback = (label: string | null) => void;

const LINE_COLOR = new Color([0, 120, 212]);
const FILL_COLOR = new Color([0, 120, 212, 0.12]);
const LOCATION_COLOR = new Color([173, 255, 47]); // verde limón

/** Escala de referencia para el tamaño del texto del punto (ajustable con el zoom). */
const LOCATION_BASE_SCALE = 577791;
const LOCATION_BASE_TEXT_SIZE = 9;
const LOCATION_MIN_TEXT_SIZE = 7;
const LOCATION_MAX_TEXT_SIZE = 14;
/** Separación en px de pantalla entre el punto y la base del texto (el texto queda encima). */
const LOCATION_TEXT_GAP_PX = 26;

let nextId = 1;

@Injectable()
export class MeasureService implements OnDestroy {
  constructor(private projectionSvc: ProjectionService) {}

  private view: MapView | null = null;
  private graphicsLayer: GraphicsLayer | null = null;
  private locationLayer: GraphicsLayer | null = null;
  private sketch: Sketch | null = null;
  private createHandle: IHandle | null = null;
  private clickHandle: IHandle | null = null;
  private cursorHandle: IHandle | null = null;
  private scaleHandle: IHandle | null = null;
  private onResult: OnResultCallback | null = null;
  private onLiveUpdate: OnLiveUpdateCallback | null = null;
  private currentUnit: MeasureUnitEnum = MeasureUnitEnum.Meters;
  private currentPerimeterUnit: MeasureUnitEnum = MeasureUnitEnum.Kilometers;
  private liveLabelGraphic: Graphic | null = null;
  private operatorsReady = false;

  readonly records: MeasureRecord[] = [];

  get initialized(): boolean { return !!this.sketch; }

  async initialize(view: MapView): Promise<void> {
    if (this.sketch) return;
    this.view = view;

    await this.ensureOperatorsLoaded();

    this.graphicsLayer = new GraphicsLayer({
      title: 'measure-layer',
      listMode: 'hide',
    });
    view.map?.add(this.graphicsLayer);

    this.locationLayer = new GraphicsLayer({
      title: 'measure-location-layer',
      listMode: 'hide',
    });
    view.map?.add(this.locationLayer);

    const lineSym = new SimpleLineSymbol({
      color: LINE_COLOR,
      width: 2.5,
      cap: 'round',
      join: 'round',
    });
    const fillSym = new SimpleFillSymbol({
      color: FILL_COLOR,
      outline: lineSym,
    });
    const pointSym = new SimpleMarkerSymbol({
      color: LINE_COLOR,
      outline: new SimpleLineSymbol({ color: new Color([255, 255, 255]), width: 1.5 }),
      size: 10,
      style: 'circle',
    });

    this.sketch = new Sketch({
      view,
      layer: this.graphicsLayer,
      creationMode: 'single',
      polylineSymbol: lineSym,
      polygonSymbol: fillSym,
      pointSymbol: pointSym,
      defaultUpdateOptions: { enableRotation: false, enableScaling: false },
      visibleElements: {
        createTools: { polygon: false, polyline: false, point: false, circle: false, rectangle: false, text: false, freehandPolygon: false, freehandPolyline: false, multipoint: false } as any,
        selectionTools: { 'lasso-selection': false, 'rectangle-selection': false } as any,
        undoRedoMenu: false,
        settingsMenu: false,
      },
    });

    this.createHandle = this.sketch.on('create', (e) => this.onCreate(e));
    this.scaleHandle = view.watch('scale', () => this.updateLocationLabelSize());
  }

  setOnResult(cb: OnResultCallback): void {
    this.onResult = cb;
  }

  setOnLiveUpdate(cb: OnLiveUpdateCallback): void {
    this.onLiveUpdate = cb;
  }

  selectTool(tool: MeasureToolEnum.Distance | MeasureToolEnum.Area, unit: MeasureUnitEnum, perimeterUnit?: MeasureUnitEnum): void {
    if (!this.sketch || !this.view) return;
    this.currentUnit = unit;
    if (perimeterUnit != null) this.currentPerimeterUnit = perimeterUnit;

    this.sketch.viewModel.cancel();
    this.sketch.viewModel.pointSymbol = this.sketch.viewModel.pointSymbol;

    this.sketch.viewModel.create(MeasureToolEnum.toGeometryType(tool)).catch(() => {
      // Sketch se cancela si ya hay un dibujo activo — es esperado
    });
  }

  setUnit(unit: MeasureUnitEnum): void {
    this.currentUnit = unit;
  }

  setPerimeterUnit(unit: MeasureUnitEnum): void {
    this.currentPerimeterUnit = unit;
  }

  formatResultValue(rawValue: number, unit: MeasureUnitEnum, type: MeasureToolEnum.Distance | MeasureToolEnum.Area): string {
    return this.smartUnit(rawValue, unit, type).label;
  }

  updateMapLabel(id: number, label: string): void {
    const rec = this.records.find(r => r.id === id);
    if (rec?.labelGraphic) {
      const sym = (rec.labelGraphic.symbol as TextSymbol).clone();
      sym.text = label;
      rec.labelGraphic.symbol = sym;
    }
  }

  deactivate(): void {
    this.sketch?.viewModel?.cancel();
    this.clearTransientState();
    this.removeLiveMapLabel();
    this.onLiveUpdate?.(null);
  }

  activateLocation(onCapture: (lat: number, lon: number) => void): void {
    this.deactivate();
    this.sketch?.viewModel?.cancel();
    if (!this.view) return;

    const container = this.view.container as HTMLElement | null;
    if (container) container.style.cursor = 'crosshair';

    this.cursorHandle = this.view.on('pointer-move', (e) => {
      const pt = this.view!.toMap({ x: e.x, y: e.y }) as Point;
      if (!pt || !pt.latitude) return;
      const lat = pt.latitude;
      const lon = pt.longitude!;
      const wgs84 = this.projectionSvc.toWgs84(lat, lon);
      const utmZone = this.projectionSvc.detectUtmZone(lon);
      const utmRaw = this.projectionSvc.toUtm(lat, lon, utmZone);
      const [eastStr, northStr] = utmRaw.split(/\s+/);
      const utmLabel = eastStr && northStr ? `${eastStr} E · ${northStr} N` : '—';
      const label = `Geográficas: ${wgs84}\nUTM (${utmZone}): ${utmLabel}`;
      this.onLiveUpdate?.(label);
      this.updateLiveMapLabel(label, pt, this.view!.spatialReference);
    });

    this.clickHandle = this.view.on('click', (e) => {
      const pt = this.view!.toMap({ x: e.x, y: e.y }) as Point;
      if (!pt || !pt.latitude) return;
      this.removeLiveMapLabel();
      onCapture(pt.latitude, pt.longitude!);
    });
  }

  private onCreate(event: any): void {
    switch (event.state) {
      case 'start': return this.onCreateStart();
      case 'active': return this.onCreateActive(event);
      case 'complete': return this.onCreateComplete(event);
    }
  }

  private onCreateStart(): void {
    this.removeLiveMapLabel();
    this.onLiveUpdate?.(null);
  }

  private onCreateActive(event: any): void {
    const geo = event.graphic?.geometry;
    if (!geo) { this.onLiveUpdate?.(null); return; }
    const text = geo.type === 'polyline'
      ? this.liveDistanceText(geo)
      : this.livePolygonText(geo);
    this.onLiveUpdate?.(text);
    const pt = this.liveLabelPoint(geo);
    if (pt) this.updateLiveMapLabel(text, pt, this.view?.spatialReference);
  }

  private onCreateComplete(event: any): void {
    this.removeLiveMapLabel();
    this.onLiveUpdate?.(null);
    const geo = event.graphic?.geometry;
    if (!geo) return;
    const isPolygon = geo.type === 'polygon';
    const id = nextId++;
    const { rawValue, unit, label, perimeterRawValue } = this.buildFinalResult(geo, isPolygon);
    const type = isPolygon ? MeasureToolEnum.Area : MeasureToolEnum.Distance;
    this.removeByType(type);
    this.records.push({ id, type, geometry: geo as any, graphic: event.graphic, labelGraphic: null });
    this.onResult?.(id, rawValue, unit, label, perimeterRawValue);
    const pt = this.pointOrArray(
      isPolygon ? (geo as any).centroid : (geo as Polyline).paths[0][0]
    );
    if (pt) {
      const g = this.makeMapLabel(pt, geo.spatialReference, label);
      this.graphicsLayer?.add(g);
      const rec = this.records[this.records.length - 1];
      if (rec) rec.labelGraphic = g;
    }
  }

  private liveDistanceText(geo: any): string {
    return this.computeResult(geo, MeasureToolEnum.Distance, this.currentUnit).label;
  }

  private livePolygonText(geo: any): string {
    const perim = this.computeResult(geo, MeasureToolEnum.Distance, this.currentPerimeterUnit).label;
    const area = this.computeResult(geo, MeasureToolEnum.Area, this.currentUnit);
    return `Perímetro: ${perim}\nÁrea: ${area.label}`;
  }

  private liveLabelPoint(geo: any): { x: number; y: number } | null {
    return this.pointOrArray(
      geo.type === 'polygon'
        ? (geo as any).centroid
        : (geo as Polyline).paths?.[0]?.[(geo as Polyline).paths[0].length - 1]
    );
  }

  private buildFinalResult(geo: any, isPolygon: boolean): { rawValue: number; unit: MeasureUnitEnum; label: string; perimeterRawValue?: number } {
    if (isPolygon) {
      const area = this.computeResult(geo, MeasureToolEnum.Area, this.currentUnit);
      const perim = this.computeResult(geo, MeasureToolEnum.Distance, this.currentPerimeterUnit);
      return { rawValue: area.rawValue, unit: area.unit, label: `Área: ${area.label}\nPerímetro: ${perim.label}`, perimeterRawValue: perim.rawValue };
    }
    return this.computeResult(geo, MeasureToolEnum.Distance, this.currentUnit);
  }

  private removeByType(type: MeasureRecord['type']): void {
    const idx = this.records.findIndex(r => r.type === type);
    if (idx === -1) return;
    const rec = this.records[idx];
    const layer = type === MeasureToolEnum.Location ? this.locationLayer : this.graphicsLayer;
    layer?.remove(rec.graphic);
    if (rec.labelGraphic) {
      const labelLayer = type === MeasureToolEnum.Location ? this.locationLayer : this.graphicsLayer;
      labelLayer?.remove(rec.labelGraphic);
    }
    this.records.splice(idx, 1);
  }

  removeLocationRecord(): void {
    this.removeByType(MeasureToolEnum.Location);
  }

  removeRecord(id: number): void {
    const idx = this.records.findIndex(r => r.id === id);
    if (idx === -1) return;
    const rec = this.records[idx];
    const layer = rec.type === MeasureToolEnum.Location ? this.locationLayer : this.graphicsLayer;
    layer?.remove(rec.graphic);
    if (rec.labelGraphic) {
      const labelLayer = rec.type === MeasureToolEnum.Location ? this.locationLayer : this.graphicsLayer;
      labelLayer?.remove(rec.labelGraphic);
    }
    this.records.splice(idx, 1);
  }

  clearAllRecords(): void {
    this.removeLiveMapLabel();
    for (const rec of this.records) {
      const layer = rec.type === MeasureToolEnum.Location ? this.locationLayer : this.graphicsLayer;
      layer?.remove(rec.graphic);
      if (rec.labelGraphic) {
        const labelLayer = rec.type === MeasureToolEnum.Location ? this.locationLayer : this.graphicsLayer;
        labelLayer?.remove(rec.labelGraphic);
      }
    }
    this.records.length = 0;
  }

  private async ensureOperatorsLoaded(): Promise<void> {
    if (this.operatorsReady) return;
    await Promise.all([
      geodeticLengthOperator.load(),
      geodeticAreaOperator.load(),
    ]);
    this.operatorsReady = true;
  }

  private computeResult(
    geometry: any, type: MeasureToolEnum.Distance | MeasureToolEnum.Area, unit: MeasureUnitEnum
  ): { rawValue: number; unit: MeasureUnitEnum; label: string } {
    if (!this.view) return { rawValue: 0, unit, label: '' };
    const sr = this.view.spatialReference;
    const useGeodesic = sr.isGeographic || sr.isWebMercator;

    if (type === MeasureToolEnum.Distance) {
      const meters = useGeodesic
        ? geodeticLengthOperator.execute(geometry, { unit: 'meters' })
        : lengthOperator.execute(geometry, { unit: 'meters' });
      if (!meters || isNaN(meters) || meters <= 0) return { rawValue: 0, unit, label: '' };
      const smart = this.smartUnit(meters, unit, type);
      return { rawValue: meters, ...smart };
    } else {
      const sqMeters = useGeodesic
        ? geodeticAreaOperator.execute(geometry, { unit: 'square-meters' })
        : areaOperator.execute(geometry, { unit: 'square-meters' });
      if (!sqMeters || isNaN(sqMeters) || sqMeters <= 0) return { rawValue: 0, unit, label: '' };
      const smart = this.smartUnit(sqMeters, unit, type);
      return { rawValue: sqMeters, ...smart };
    }
  }

  /** Sugiere la unidad óptima según la magnitud del valor */
  suggestOptimalUnit(
    rawValue: number, type: MeasureToolEnum.Distance | MeasureToolEnum.Area
  ): MeasureUnitEnum {
    if (type === MeasureToolEnum.Distance) {
      if (rawValue >= 1000) return MeasureUnitEnum.Kilometers;
      if (rawValue >= 0.5) return MeasureUnitEnum.Meters;
      return MeasureUnitEnum.Meters;
    }
    if (rawValue >= 1e6) return MeasureUnitEnum.SquareKilometers;
    if (rawValue >= 1e4) return MeasureUnitEnum.Hectares;
    if (rawValue >= 4e3) return MeasureUnitEnum.Acres;
    return MeasureUnitEnum.SquareMeters;
  }

  private smartUnit(
    value: number, preferred: MeasureUnitEnum, type: MeasureToolEnum.Distance | MeasureToolEnum.Area
  ): { unit: MeasureUnitEnum; label: string } {
    if (type === MeasureToolEnum.Distance) {
      return { unit: preferred, label: this.formatLinear(value, preferred) };
    }
    return { unit: preferred, label: this.formatArea(value, preferred) };
  }

  private pointOrArray(p: any): { x: number; y: number } | null {
    if (!p) return null;
    if (Array.isArray(p)) return { x: p[0], y: p[1] };
    if (p.x != null) return { x: p.x, y: p.y };
    return null;
  }

  private makeMapLabel(pt: { x: number; y: number }, sr: any, text: string, location = false): Graphic {
    return new Graphic({
      geometry: new Point({ x: pt.x, y: pt.y, spatialReference: sr }),
      symbol: new TextSymbol({
        text,
        color: new Color([255, 255, 255]),
        haloColor: new Color([0, 0, 0, 0.75]),
        haloSize: 3,
        font: { size: location ? this.currentLocationTextSize : 11, family: "'SF Mono', 'JetBrains Mono', 'Cascadia Code', monospace", weight: 'bold' },
        xoffset: 0,
        verticalAlignment: location ? 'bottom' : 'baseline',
        yoffset: location ? 0 : -14,
      }),
    });
  }

  /** Devuelve un punto en coordenadas de mapa ubicado `LOCATION_TEXT_GAP_PX` px arriba del punto dado (espacio de pantalla). */
  private locationLabelAnchor(pt: Point): Point {
    if (!this.view) return pt;
    const screen = this.view.toScreen(pt);
    if (!screen) return pt;
    const above = this.view.toMap({ x: screen.x, y: screen.y - LOCATION_TEXT_GAP_PX });
    return new Point({ x: above.x, y: above.y, spatialReference: this.view.spatialReference });
  }

  showLocationPoint(pt: Point, label: string): void {
    this.removeByType(MeasureToolEnum.Location);
    const sym = new SimpleMarkerSymbol({
      color: LOCATION_COLOR,
      outline: new SimpleLineSymbol({ color: new Color([255, 255, 255]), width: 2 }),
      size: 14,
      style: 'circle',
    });
    const g = new Graphic({ geometry: pt, symbol: sym });
    this.locationLayer?.add(g);
    const id = nextId++;
    this.records.push({ id, type: MeasureToolEnum.Location, geometry: pt, graphic: g, labelGraphic: null });
    const lg = this.makeMapLabel(this.locationLabelAnchor(pt), pt.spatialReference, label, true);
    this.locationLayer?.add(lg);
    const rec = this.records[this.records.length - 1];
    if (rec) rec.labelGraphic = lg;
  }

  /** Tamaño de fuente actual del texto del punto, ajustable según el zoom. */
  private get currentLocationTextSize(): number {
    return this.computeLocationTextSize();
  }

  /** Calcula el tamaño de fuente para el texto del punto según la escala de la vista. */
  private computeLocationTextSize(): number {
    const scale = this.view?.scale ?? LOCATION_BASE_SCALE;
    const size = LOCATION_BASE_TEXT_SIZE * (LOCATION_BASE_SCALE / scale);
    return Math.min(LOCATION_MAX_TEXT_SIZE, Math.max(LOCATION_MIN_TEXT_SIZE, Math.round(size)));
  }

  /** Ajusta el tamaño de fuente y la posición de los labels de punto existentes al cambiar el zoom. */
  private updateLocationLabelSize(): void {
    for (const rec of this.records) {
      if (rec.type !== MeasureToolEnum.Location || !rec.labelGraphic) continue;
      const sym = (rec.labelGraphic.symbol as TextSymbol).clone();
      sym.font = { ...sym.font, size: this.currentLocationTextSize };
      sym.verticalAlignment = 'bottom';
      sym.yoffset = 0;
      rec.labelGraphic.symbol = sym;
      rec.labelGraphic.geometry = this.locationLabelAnchor(rec.geometry as Point);
    }
    if (this.liveLabelGraphic) {
      const sym = (this.liveLabelGraphic.symbol as TextSymbol).clone();
      sym.font = { ...sym.font, size: this.currentLocationTextSize };
      sym.verticalAlignment = 'bottom';
      sym.yoffset = 0;
      this.liveLabelGraphic.symbol = sym;
    }
  }

  private updateLiveMapLabel(text: string, pt: any, sr: any): void {
    if (!this.locationLayer) return;
    this.removeLiveMapLabel();
    const anchor = this.locationLabelAnchor(pt as Point);
    this.liveLabelGraphic = new Graphic({
      geometry: new Point({ x: anchor.x, y: anchor.y, spatialReference: sr }),
      symbol: new TextSymbol({
        text,
        color: new Color([255, 255, 255]),
        haloColor: new Color([0, 0, 0, 0.55]),
        haloSize: 2,
        font: { size: this.currentLocationTextSize, family: "'SF Mono', 'JetBrains Mono', 'Cascadia Code', monospace", weight: 'bold' },
        xoffset: 0,
        verticalAlignment: 'bottom',
        yoffset: 0,
      }),
    });
    this.locationLayer?.add(this.liveLabelGraphic);
  }

  private removeLiveMapLabel(): void {
    if (this.liveLabelGraphic) {
      this.locationLayer?.remove(this.liveLabelGraphic);
      this.liveLabelGraphic = null;
    }
  }

  private clearTransientState(): void {
    this.clickHandle?.remove();
    this.cursorHandle?.remove();
    const container = this.view?.container as HTMLElement | null;
    if (container) container.style.cursor = 'default';
  }

  private formatLinear(meters: number, unit: MeasureUnitEnum): string {
    if (MeasureUnitEnum.isArea(unit)) unit = MeasureUnitEnum.Meters;
    switch (unit) {
      case MeasureUnitEnum.Kilometers: return (meters / 1000).toFixed(2) + ' km';
      case MeasureUnitEnum.Miles: return (meters / 1609.344).toFixed(2) + ' mi';
      case MeasureUnitEnum.Feet: return (meters / 0.3048).toFixed(0) + ' ft';
      case MeasureUnitEnum.Yards: return (meters / 0.9144).toFixed(0) + ' yd';
      default: return meters.toFixed(1) + ' m';
    }
  }

  private formatArea(sqMeters: number, unit: MeasureUnitEnum): string {
    if (MeasureUnitEnum.isLinear(unit)) unit = MeasureUnitEnum.SquareMeters;
    switch (unit) {
      case MeasureUnitEnum.Hectares: return (sqMeters / 10000).toFixed(2) + ' ha';
      case MeasureUnitEnum.Acres: return (sqMeters / 4046.8564224).toFixed(2) + ' ac';
      case MeasureUnitEnum.SquareKilometers: return (sqMeters / 1e6).toFixed(3) + ' km²';
      case MeasureUnitEnum.SquareMiles: return (sqMeters / 2.59e6).toFixed(3) + ' mi²';
      case MeasureUnitEnum.SquareFeet: return (sqMeters / 0.092903).toFixed(0) + ' ft²';
      case MeasureUnitEnum.SquareYards: return (sqMeters / 0.836127).toFixed(0) + ' yd²';
      default: return sqMeters.toFixed(1) + ' m²';
    }
  }

  ngOnDestroy(): void {
    this.createHandle?.remove();
    this.scaleHandle?.remove();
    this.deactivate();
    this.clearAllRecords();
    if (this.graphicsLayer) {
      this.view?.map?.remove(this.graphicsLayer);
    }
    if (this.locationLayer) {
      this.view?.map?.remove(this.locationLayer);
    }
  }
}
